import React from "react";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@racoon/ui";
import { IcAi, IcPlus, IcCancel } from "@racoon/icons";
import { useTypedT, Namespace } from "@racoon/i18n";

interface TripTabsProps {
  trips: { id: string }[];
  activeTripId: string;
  onSelectTrip: (id: string) => void;
  onAddTrip: () => void;
  onRemoveTrip: () => void;
  onOptimize: () => void;
  // Optimization needs an unloading point (and jobs to distribute) before it can run.
  canOptimize?: boolean;
  isOptimizing?: boolean;
}

export const TripTabs: React.FC<TripTabsProps> = ({
  trips,
  activeTripId,
  onSelectTrip,
  onAddTrip,
  onRemoveTrip,
  onOptimize,
  canOptimize = false,
  isOptimizing = false,
}) => {
  const t = useTypedT(Namespace.Routes);

  return (
    <div className="flex items-center justify-between p-3 border rounded-t-xl gap-2">
      <div className="flex min-w-0 items-center gap-1 overflow-x-auto rounded-xl bg-brand-blue-light p-1">
        {trips.map((trip, idx) => {
          const isActive = trip.id === activeTripId;
          return (
            <button
              key={trip.id}
              type="button"
              onClick={() => onSelectTrip(trip.id)}
              className={`min-w-[72px] shrink-0 rounded-lg h-6 text-sm transition-colors ${
                isActive
                  ? "bg-brand-blue text-white shadow-sm"
                  : " text-text-primary hover:bg-white/40"
              }`}
            >
              {t("tripIndex", { index: idx + 1 })}
            </button>
          );
        })}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-gray-100 focus:outline-none"
            aria-label={t("addTrip")}
          >
            <MoreHorizontal className="h-4 w-4 text-black" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem
            onClick={onOptimize}
            disabled={!canOptimize || isOptimizing}
            title={canOptimize ? undefined : t("optimizeSelectDepotFirst")}
            className="gap-2"
          >
            <IcAi className="h-5 w-5 text-brand-purple" />
            <span>{isOptimizing ? t("optimizing") : t("optimize")}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onAddTrip} className="gap-2">
            <IcPlus className="h-5 w-5 text-brand-blue" />
            <span>{t("addTrip")}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onRemoveTrip} className="gap-2">
            <IcCancel className="h-5 w-5 text-brand-red" />
            <span>{t("removeTrip")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
